// PocketStrip — HUD widget rendering POCKET_CAPACITY slots in a
// horizontal strip. Lives in RunUIScene which subscribes to
// RunScene.events `pocket:changed` and re-renders by passing the event
// payload's `pocket` array straight to `render(pocket)`.
//
// Stateless aside from its slot views: render(pocket) is called for
// every event and re-fills every slot from the pocket array — no local
// mirror of pocket state (RESEARCH Anti-Pattern 7).
//
// Slot fill animation: D-11 (slot scales 0.8 → 1.0 over 80 ms on add).
// Tweening every slot every render is fine at Phase 1's call frequency
// (≤ 7 events per run). Phase 4 (juice) will refine.
//
// CLAUDE.md anti-pattern note: visual constants only — strip layout,
// slot dims, suit palette. Same exemption as CardPickup.

import Phaser from 'phaser';
import type { Card } from '../run/types';
import { POCKET_CAPACITY } from '../systems/PocketSystem';

const SLOT_W = 14;
const SLOT_H = 20;
const SLOT_GAP = 2;
const STRIP_X = 6;
const STRIP_Y = 6;

const EMPTY_SLOT_FILL = 0x222033;
const STROKE_COLOR = 0x08050a;
const LABEL_COLOR = '#08050a';

const SUIT_COLOR: Record<Card['suit'], number> = {
  h: 0xff5566,
  d: 0xff5566,
  c: 0xf5e1c8,
  s: 0xf5e1c8,
};

interface SlotView {
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
}

export class PocketStrip {
  private readonly scene: Phaser.Scene;
  private readonly slots: SlotView[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    for (let i = 0; i < POCKET_CAPACITY; i++) {
      const x = STRIP_X + i * (SLOT_W + SLOT_GAP);
      const bg = scene.add
        .rectangle(x, STRIP_Y, SLOT_W, SLOT_H, EMPTY_SLOT_FILL, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, STROKE_COLOR);
      const label = scene.add
        .text(x + SLOT_W / 2, STRIP_Y + SLOT_H / 2, '', {
          fontFamily: 'monospace',
          fontSize: '8px',
          color: LABEL_COLOR,
        })
        .setOrigin(0.5);
      this.slots.push({ bg, label });
    }
  }

  render(pocket: Card[]): void {
    for (let i = 0; i < POCKET_CAPACITY; i++) {
      const slot = this.slots[i];
      const card = pocket[i];
      if (card) {
        slot.bg.setFillStyle(SUIT_COLOR[card.suit], 1);
        slot.label.setText(`${card.rank}${card.suit}`);
        // D-11 slot fill: scale 0.8 → 1.0 over 80 ms.
        slot.bg.setScale(0.8);
        slot.label.setScale(0.8);
        this.scene.tweens.add({
          targets: [slot.bg, slot.label],
          scale: 1,
          duration: 80,
          ease: 'Quad.out',
        });
      } else {
        slot.bg.setFillStyle(EMPTY_SLOT_FILL, 1);
        slot.label.setText('');
        slot.bg.setScale(1);
        slot.label.setScale(1);
      }
    }
  }
}
